// InvoiceForm.tsx
import React, { ChangeEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from './InputField';
import ContentField from './ContentField';
import ToggleGroup from './ToggleGroup';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import { GetInvoiceFormRowTitleFlex } from '../util';
import { FormData, getInvoiceFormToggleOptions, InvoiceFormCreateRequiredFields, invoiceFormToggleOptions, isEnterpriseSelectionRestricted } from '../util/enum';

interface InvoiceFormProps {
    formData: FormData;
    handleInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleAlignment: (event: React.MouseEvent<HTMLElement>, newAlignment: 'enterprise' | 'personal') => void;
    missingFields?: string[];
    invoiceDetail: any[];
    alignment?: string;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ formData, handleInputChange, handleAlignment, missingFields, invoiceDetail }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isToggleRestricted = useMemo(() => isEnterpriseSelectionRestricted(formData?.SHOPID, formData?.SHOPNAME, formData?.INVOICEDATE),
        [formData?.SHOPID, formData?.SHOPNAME, formData?.INVOICEDATE]);
    const TITLE_BASE = 'invoiceForm.inputField.title.';
    const PLACEHOLDER_BASE = 'invoiceForm.inputField.placeholder.';

    const toggleOptions = Object.values(getInvoiceFormToggleOptions({ isToggleRestricted })).map((option) => ({
        value: option.value,
        label: t(option.label)
    }));

    const createInputField = (name: string) => ({
        name,
        title: t(`${TITLE_BASE}${name}`),
        placeholder: t(`${PLACEHOLDER_BASE}${name}`),
    });

    const inputFields = (formData?.alignment === 'personal'
        ? ['TaxPersonal', 'TaxEmail', 'TaxAmount', 'TaxRemark']
        : ['TaxCompany', 'TaxNo', 'TaxEmail', 'TaxAmount', 'TaxRemark']
    ).map(createInputField);

    const renderInputField = (field: { name: string, title: string, placeholder: string }) => (
        <InputField
            key={`inputField_${field?.title}_${field?.name}`}
            name={field.name}
            value={(formData?.[field?.name] || '').toString()}
            onChange={handleInputChange}
            flexDirection='row'
            description={field.name === 'TaxRemark' ? t('invoiceForm.inputField.text.invoiceRemarkDesc') : undefined}
            typographySx={{ flex: GetInvoiceFormRowTitleFlex(isSmallScreen), textAlign: 'left' }}
            placeholder={field.placeholder}
            label={
                <span>
                    {field.title}
                    {InvoiceFormCreateRequiredFields?.[formData?.alignment]?.includes(field.name) && (
                        <Typography component="span" style={{ color: 'red' }}>
                            *
                        </Typography>
                    )}
                </span>
            }
            missingFields={missingFields}
            helperText={(field: string) => t('error.pleaseFillInRequiredFields', { field: t(`invoiceForm.inputField.title.${field}`) })}
            showLabelAsTypography={false}
            type={field.name === 'TaxAmount' ? 'number' : field.name === 'TaxEmail' ? 'email' : 'text'} // Set the type prop based on the field name
            inputProps={field.name === 'TaxAmount' ? { min: 0 } : undefined} // Prevent value with negative numbers
        />
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
                <ContentField
                    content={t('invoiceForm.text.taxInvoice')}
                    title={t('invoiceForm.text.invoiceCategory')}
                    contentSx={{ textAlign: 'left' }}
                    titleSx={{ textAlign: 'left' }}
                    direction='column'
                    gridContainerSx={{
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                    }}
                />
            </Grid>
            <Grid item xs={12} sm={12}>
                <ContentField
                    direction={'column'}
                    content={<ToggleGroup
                        value={formData?.alignment || ''}
                        options={toggleOptions}
                        onChange={(event: any, newAlignment: any) => handleAlignment(event, newAlignment)}
                        isToggleRestricted={isToggleRestricted}
                    />}
                    title={t('invoiceForm.text.invoiceCategory')}
                    titleSx={{ textAlign: 'left' }}
                    gridContainerSx={{ alignItems: 'flex-start', justifyContent: 'flex-start' }}
                />
            </Grid>
            {inputFields.map((field, index) => (
                <Grid item xs={12} sm={12} key={index}>
                    {renderInputField(field)}
                </Grid>
            ))}
        </Grid>
    );
}

export default InvoiceForm;